// Self-service two-factor sign-in (2026-10-09; backend routers/auth.py
// /api/auth/mfa*, onboarding.py). Opened from the profile popover:
//   off -> "Turn on": QR code (server-rendered SVG) + setup key + a code
//          from the app; recovery codes are shown ONCE afterwards
//   on  -> "Set up a new phone" (password + current code, then the new QR),
//          "New recovery codes", "Turn off" (password + current code).
// A current code can always be a recovery code. Admins reset another user's
// two-factor from Admin > Users.
import { ApiError, getJson, postJson } from "./api";
import { el } from "./legend";
import { qrNode } from "./onboarding";
import { getState, setState } from "./state";

interface MfaStatus {
  enabled: boolean;
  recovery_codes_left: number;
}

interface MfaSetupInfo {
  secret: string;
  otpauth_uri: string;
  qr_svg?: string;
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return "Too many attempts. Please wait 15 minutes.";
    if (typeof e.detail === "string") return e.detail;
  }
  return e instanceof Error ? e.message : String(e);
}

function inputEl(type: string, placeholder: string, autocomplete: string): HTMLInputElement {
  const i = el("input", { type, placeholder }) as HTMLInputElement;
  i.setAttribute("autocomplete", autocomplete);
  return i;
}

function setMfaFlag(on: boolean): void {
  const u = getState().currentUser;
  if (u) setState({ currentUser: { ...u, mfa_enabled: on } });
}

function recoveryCodesBlock(codes: string[]): HTMLElement {
  const box = el("div", { className: "mfa-recovery" });
  box.append(
    el("p", {}, "Your recovery codes. Each works once, in place of a code from your phone. Save them somewhere safe now: they won't be shown again."),
  );
  const pre = el("pre", { className: "onb-secret" }, codes.join("\n"));
  const copy = el("button", { type: "button" }, "Copy codes");
  copy.addEventListener("click", () => {
    void navigator.clipboard?.writeText(codes.join("\n")).then(() => (copy.textContent = "Copied"));
  });
  box.append(pre, copy);
  return box;
}

/** QR + key + code input for a fresh secret; resolves the confirmed pair. */
function setupStep(host: HTMLElement, onConfirm: (secret: string, code: string) => Promise<void>): void {
  host.append(el("p", { className: "caption" }, "Getting a setup code…"));
  postJson<MfaSetupInfo>("/api/auth/mfa/setup", {})
    .then((s) => {
      host.innerHTML = "";
      host.append(el("p", {}, "Scan this QR code with an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password, ...):"));
      const qr = qrNode(s.qr_svg);
      if (qr) host.append(qr);
      const key = el("code", { className: "onb-secret" }, s.secret.replace(/(.{4})/g, "$1 ").trim());
      host.append(el("p", { className: "caption" }, "Or type this setup key into the app:"), key);
      const code = inputEl("text", "123456", "one-time-code");
      code.inputMode = "numeric";
      code.maxLength = 7;
      const btn = el("button", { type: "submit" }, "Confirm");
      const status = el("span", { className: "caption" });
      const form = el("form");
      form.append(el("label", {}, "Code from the app "), code, btn, status);
      form.addEventListener("submit", (ev) => {
        ev.preventDefault();
        btn.disabled = true;
        status.textContent = "";
        onConfirm(s.secret, code.value.replace(/\s/g, ""))
          .catch((e) => {
            status.textContent = errText(e);
          })
          .finally(() => (btn.disabled = false));
      });
      host.append(form);
      code.focus();
    })
    .catch((e) => {
      host.innerHTML = "";
      host.append(el("p", { className: "caption" }, errText(e)));
    });
}

/** Password + current code form; calls `go` with them. */
function reauthForm(label: string, go: (password: string, code: string) => Promise<void>): HTMLElement {
  const form = el("form", { className: "mfa-reauth" });
  const pw = inputEl("password", "Password", "current-password");
  const code = inputEl("text", "Current code or recovery code", "one-time-code");
  code.maxLength = 16;
  const btn = el("button", { type: "submit" }, label);
  const status = el("span", { className: "caption" });
  form.append(pw, code, btn, status);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    btn.disabled = true;
    status.textContent = "";
    go(pw.value, code.value.replace(/\s/g, ""))
      .catch((e) => (status.textContent = errText(e)))
      .finally(() => (btn.disabled = false));
  });
  return form;
}

function render(body: HTMLElement, st: MfaStatus): void {
  body.innerHTML = "";
  if (!st.enabled) {
    body.append(
      el("p", {}, "Two-factor sign-in is off. Turning it on means a code from your phone is needed as well as your password."),
    );
    const start = el("button", { type: "button" }, "Turn on two-factor sign-in");
    const step = el("div");
    start.addEventListener("click", () => {
      start.remove();
      setupStep(step, (secret, code) =>
        postJson<{ recovery_codes: string[] }>("/api/auth/mfa/enable", { secret, code }).then((r) => {
          setMfaFlag(true);
          body.innerHTML = "";
          body.append(el("p", {}, "Two-factor sign-in is on."), recoveryCodesBlock(r.recovery_codes));
        }),
      );
    });
    body.append(start, step);
    return;
  }
  body.append(el("p", {}, `Two-factor sign-in is on. Recovery codes left: ${st.recovery_codes_left}.`));

  const newPhone = el("details");
  newPhone.append(el("summary", {}, "Set up a new phone"));
  const npStep = el("div");
  newPhone.append(
    el("p", { className: "caption" }, "Confirm it's you, then scan the new QR code. The old phone stops working."),
    reauthForm("Continue", async (password, currentCode) => {
      npStep.innerHTML = "";
      setupStep(npStep, (secret, code) =>
        postJson<{ recovery_codes: string[] }>("/api/auth/mfa/regenerate", {
          password,
          current_code: currentCode,
          secret,
          code,
        }).then((r) => {
          body.innerHTML = "";
          body.append(el("p", {}, "Your new phone is set up."), recoveryCodesBlock(r.recovery_codes));
        }),
      );
    }),
    npStep,
  );

  const codes = el("details");
  codes.append(el("summary", {}, "New recovery codes"));
  const codesOut = el("div");
  codes.append(
    el("p", { className: "caption" }, "Replaces all your recovery codes."),
    reauthForm("Make new codes", (password, code) =>
      postJson<{ recovery_codes: string[] }>("/api/auth/mfa/recovery-codes", { password, current_code: code }).then((r) => {
        codesOut.replaceChildren(recoveryCodesBlock(r.recovery_codes));
      }),
    ),
    codesOut,
  );

  const off = el("details");
  off.append(el("summary", {}, "Turn off two-factor sign-in"));
  off.append(
    reauthForm("Turn off", (password, code) =>
      postJson("/api/auth/mfa/disable", { password, current_code: code }).then(() => {
        setMfaFlag(false);
        render(body, { enabled: false, recovery_codes_left: 0 });
      }),
    ),
  );
  body.append(newPhone, codes, off);
}

/** The two-factor dialog (modal). */
export function openAccountSecurity(): void {
  document.getElementById("account-security")?.remove();
  const overlay = el("div", { id: "account-security", className: "modal-overlay" });
  const dlg = el("div", { className: "modal account-security" });
  dlg.setAttribute("role", "dialog");
  dlg.setAttribute("aria-modal", "true");
  const close = el("button", { type: "button", className: "modal-close", title: "Close" }, "×");
  close.addEventListener("click", () => overlay.remove());
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });
  const body = el("div");
  dlg.append(close, el("h2", {}, "Two-factor sign-in"), body);
  overlay.append(dlg);
  document.body.append(overlay);
  body.append(el("p", { className: "caption" }, "Loading…"));
  getJson<MfaStatus>("/api/auth/mfa")
    .then((st) => render(body, st))
    .catch((e) => body.replaceChildren(el("p", { className: "caption" }, errText(e))));
}
